import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  Typography,
  Button,
  Box,
  Autocomplete,
  TextField,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageInfoDrawer, PageShell, DataTable, type DataTableColumn } from '../../components';
import { InlineFilterBar } from '../../components/common';
import { Certificate } from '../../types/certificate';
import { certificateService } from '../../services/certificateService';
import { companyService } from '../../services/companyService';
import { CertificateModal, CertificateStatusChip } from './components';

interface CompanyOption {
  id: number;
  name: string;
}

const formatDate = (dateString: string): string => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('pt-BR');
};

const formatCNPJ = (cnpj: string): string => {
  const clean = cnpj.replace(/\D/g, '');
  if (clean.length !== 14) return cnpj;
  return clean.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const formatIssuer = (issuer: string): string => {
  const cnMatch = issuer.match(/CN=([^,]+)/);
  return cnMatch ? cnMatch[1]! : issuer;
};

const columns: DataTableColumn<Certificate>[] = [
  {
    id: 'cnpj',
    label: 'CNPJ',
    primary: true,
    render: (certificate) => (
      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
        {certificate.cnpj ? formatCNPJ(certificate.cnpj) : '-'}
      </Typography>
    ),
  },
  {
    id: 'issuer',
    label: 'Emissor',
    render: (certificate) => (
      <Typography variant="body2">
        {certificate.issuer ? formatIssuer(certificate.issuer) : '-'}
      </Typography>
    ),
  },
  {
    id: 'validFrom',
    label: 'Válido De',
    align: 'center',
    render: (certificate) => (
      <Typography variant="body2">{formatDate(certificate.validFrom ?? '')}</Typography>
    ),
  },
  {
    id: 'validTo',
    label: 'Válido Até',
    align: 'center',
    render: (certificate) => (
      <Typography variant="body2">{formatDate(certificate.validTo ?? '')}</Typography>
    ),
  },
  {
    id: 'status',
    label: 'Status',
    align: 'center',
    render: (certificate) => <CertificateStatusChip status={certificate.status} />,
  },
  {
    id: 'originalFileName',
    label: 'Arquivo',
    render: (certificate) => (
      <Typography variant="body2">{certificate.originalFileName}</Typography>
    ),
  },
];

export const CertificateListPage: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [filterCompany, setFilterCompany] = useState<CompanyOption | null>(null);

  useEffect(() => {
    companyService.getCompaniesForDropdown().then(setCompanies).catch(() => {
      setCompanies([]);
    });
  }, []);

  const loadCertificates = useCallback(async () => {
    setLoading(true);
    try {
      const filters = filterCompany ? { companyId: filterCompany.id } : undefined;
      const result = await certificateService.list(filters);
      setCertificates(result.data);
      setTotal(result.total);
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message
        ?? (err instanceof Error ? err.message : 'Erro ao carregar certificados');
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterCompany, enqueueSnackbar]);

  useEffect(() => {
    loadCertificates();
  }, [loadCertificates]);

  const handleDelete = async (id: number) => {
    try {
      await certificateService.remove(id);
      enqueueSnackbar('Certificado excluído com sucesso', { variant: 'success' });
      loadCertificates();
    } catch (err: unknown) {
      const message = (err as { message?: string })?.message
        ?? (err instanceof Error ? err.message : 'Erro ao excluir certificado');
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleUploadSuccess = () => {
    enqueueSnackbar('Certificado enviado com sucesso', { variant: 'success' });
    loadCertificates();
  };

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/' },
        { label: 'Certificados Digitais' },
      ]}
      title="Certificados Digitais"
      actions={
        <>
          <PageInfoDrawer title="Certificados Digitais">
              <Typography variant="subtitle1" gutterBottom>
                O que é?
              </Typography>
              <Typography variant="body2" paragraph>
                O certificado digital <strong>A1</strong> é necessário para assinar e
                transmitir eventos ao <strong>eSocial</strong>. Ele identifica
                digitalmente a empresa perante o governo.
              </Typography>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Formatos aceitos
              </Typography>
              <Typography variant="body2" paragraph>
                São aceitos arquivos <code>.pfx</code> e <code>.p12</code>. A senha do
                certificado é obrigatória e será usada para descriptografá-lo no momento
                da transmissão.
              </Typography>

              <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                Vinculação à empresa
              </Typography>
              <Typography variant="body2" paragraph>
                Cada certificado fica vinculado a uma empresa (CNPJ) e é utilizado para
                transmitir os eventos de Saúde e Segurança do Trabalho (SST), como:
              </Typography>
              <List dense disablePadding sx={{ pl: 2, mb: 2 }}>
                <ListItem sx={{ display: 'list-item', listStyleType: 'disc', py: 0 }}>
                  <ListItemText primary="S-2210 — Comunicação de Acidente de Trabalho (CAT)" />
                </ListItem>
                <ListItem sx={{ display: 'list-item', listStyleType: 'disc', py: 0 }}>
                  <ListItemText primary="S-2220 — Monitoramento da Saúde do Trabalhador (ASO)" />
                </ListItem>
                <ListItem sx={{ display: 'list-item', listStyleType: 'disc', py: 0 }}>
                  <ListItemText primary="S-2240 — Condições Ambientais do Trabalho (Agentes Nocivos)" />
                </ListItem>
                <ListItem sx={{ display: 'list-item', listStyleType: 'disc', py: 0 }}>
                  <ListItemText primary="S-2245 — Treinamentos, Capacitações e Exercícios Simulados" />
                </ListItem>
              </List>

              <Alert severity="warning" sx={{ mt: 2 }}>
                <strong>Atenção ao vencimento:</strong> certificados A1 têm validade
                de 1 ano. A tela lista a data de expiração de cada certificado —
                renove antes do vencimento para evitar falhas na transmissão dos
                eventos.
              </Alert>
          </PageInfoDrawer>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setModalOpen(true)}
          >
            Upload
          </Button>
        </>
      }
    >
        <Box sx={{ mb: 2 }}>
          <InlineFilterBar
            resultCount={total}
            resultNoun="certificados"
            chips={
              filterCompany
                ? [
                    {
                      key: 'companyId',
                      label: 'Empresa',
                      value: filterCompany.name,
                      onDelete: () => setFilterCompany(null),
                    },
                  ]
                : []
            }
            onClearAll={() => setFilterCompany(null)}
            loading={loading}
          >
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => option.name}
              value={filterCompany}
              onChange={(_e, value) => setFilterCompany(value)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              size="small"
              sx={{ minWidth: 190 }}
              renderInput={(params) => <TextField {...params} label="Filtrar por empresa" />}
            />
          </InlineFilterBar>
        </Box>

        <DataTable
          columns={columns}
          rows={certificates}
          getRowId={(certificate) => certificate.id}
          loading={loading}
          emptyState={{
            title: 'Nenhum certificado encontrado',
            description: 'O certificado digital A1 que assina os eventos do eSocial é enviado nesta tela, pelo botão de upload.',
          }}
          actions={[
            {
              icon: <DeleteIcon fontSize="small" />,
              tooltip: 'Excluir',
              color: 'error',
              onClick: (certificate) => handleDelete(certificate.id),
            },
          ]}
        />

        <CertificateModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          onSuccess={handleUploadSuccess}
          companies={companies}
        />
    </PageShell>
  );
};

export default CertificateListPage;
